/**
 * Performance Validator for Visual Feedback System
 * Monitors and validates 60fps performance targets with real-time metrics
 */

export interface PerformanceValidationConfig {
  targetFrameRate: number;
  measurementDuration: number; // ms
  acceptableDropThreshold: number;
  enableContinuousMonitoring: boolean;
  enablePerformanceReporting: boolean;
}

export interface PerformanceValidationResult {
  frameRate: number;
  frameDrops: number;
  averageFrameTime: number;
  worstFrameTime: number;
  percentile95FrameTime: number;
  isTargetMet: boolean;
  confidence: number;
  recommendations: string[];
}

export interface RealTimeMetrics {
  currentFrameRate: number;
  recentFrameDrops: number;
  animationLatency: number;
  memoryPressure: number;
  gpuUtilization: number;
  cpuUtilization: number;
}

/**
 * Performance validator with comprehensive 60fps monitoring
 */
export class PerformanceValidator {
  private config: PerformanceValidationConfig;
  private isMonitoring: boolean = false;
  private frameTimestamps: number[] = [];
  private animationFrameId: number | null = null;
  private validationResults: PerformanceValidationResult[] = [];
  private realTimeMetrics: RealTimeMetrics;

  // Performance tracking
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private droppedFrames: number = 0;
  private measurementStartTime: number = 0;

  // Callbacks
  public onPerformanceUpdate?: (metrics: RealTimeMetrics) => void;
  public onTargetMissed?: (result: PerformanceValidationResult) => void;
  public onValidationComplete?: (result: PerformanceValidationResult) => void;

  constructor(config: Partial<PerformanceValidationConfig> = {}) {
    this.config = {
      targetFrameRate: 60,
      measurementDuration: 3000, // Reduced to 3 seconds
      acceptableDropThreshold: 5, // 5 dropped frames
      enableContinuousMonitoring: false, // Disabled to prevent infinite loops
      enablePerformanceReporting: process.env.NODE_ENV === "development", // Only in dev mode
      ...config,
    };

    this.realTimeMetrics = {
      currentFrameRate: 0,
      recentFrameDrops: 0,
      animationLatency: 0,
      memoryPressure: 0,
      gpuUtilization: 0,
      cpuUtilization: 0,
    };
  }

  /**
   * Start performance validation
   */
  public startValidation(): void {
    if (this.isMonitoring) {
      console.warn("Performance validation already running");
      return;
    }

    this.isMonitoring = true;
    this.resetMetrics();
    this.measurementStartTime = performance.now();

    this.startFrameRateMonitoring();

    // Schedule validation completion
    setTimeout(() => {
      this.completeValidation();
    }, this.config.measurementDuration);

    console.log(
      `Started performance validation for ${this.config.measurementDuration}ms`,
    );
  }

  /**
   * Stop performance validation
   */
  public stopValidation(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    console.log("Performance validation stopped");
  }

  /**
   * Reset performance metrics
   */
  private resetMetrics(): void {
    this.frameTimestamps = [];
    this.frameCount = 0;
    this.droppedFrames = 0;
    this.lastFrameTime = 0;
    this.realTimeMetrics = {
      currentFrameRate: 0,
      recentFrameDrops: 0,
      animationLatency: 0,
      memoryPressure: 0,
      gpuUtilization: 0,
      cpuUtilization: 0,
    };
  }

  /**
   * Start frame rate monitoring using requestAnimationFrame
   */
  private startFrameRateMonitoring(): void {
    const measureFrame = (timestamp: number) => {
      if (!this.isMonitoring) {
        this.animationFrameId = null;
        return;
      }

      // Check if we've exceeded the measurement duration
      if (
        timestamp - this.measurementStartTime >
        this.config.measurementDuration
      ) {
        this.completeValidation();
        return;
      }

      // Record frame timestamp
      this.frameTimestamps.push(timestamp);
      this.frameCount++;

      // Calculate frame time
      if (this.lastFrameTime > 0) {
        const frameTime = timestamp - this.lastFrameTime;

        // Check for dropped frames (frame time > 1.5x target)
        const targetFrameTime = 1000 / this.config.targetFrameRate;
        if (frameTime > targetFrameTime * 1.5) {
          this.droppedFrames++;
        }

        // Update real-time metrics (but less frequently to reduce overhead)
        if (this.frameCount % 10 === 0) {
          // Only update every 10 frames
          this.updateRealTimeMetrics(frameTime);
        }
      }

      this.lastFrameTime = timestamp;

      // Continue monitoring only if still needed
      if (this.isMonitoring) {
        this.animationFrameId = requestAnimationFrame(measureFrame);
      } else {
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(measureFrame);
  }

  /**
   * Update real-time performance metrics
   */
  private updateRealTimeMetrics(frameTime: number): void {
    // Calculate current frame rate (based on recent frames)
    const recentFrames = this.frameTimestamps.slice(-60); // Last 60 frames
    if (recentFrames.length >= 2) {
      const duration = recentFrames[recentFrames.length - 1] - recentFrames[0];
      this.realTimeMetrics.currentFrameRate =
        ((recentFrames.length - 1) * 1000) / duration;
    }

    // Calculate recent frame drops
    const recentDropWindow = 30; // Last 30 frames
    if (this.frameCount >= recentDropWindow) {
      const recentFrameData = this.frameTimestamps.slice(-recentDropWindow);
      let recentDrops = 0;
      const targetFrameTime = 1000 / this.config.targetFrameRate;

      for (let i = 1; i < recentFrameData.length; i++) {
        const frameTime = recentFrameData[i] - recentFrameData[i - 1];
        if (frameTime > targetFrameTime * 1.5) {
          recentDrops++;
        }
      }
      this.realTimeMetrics.recentFrameDrops = recentDrops;
    }

    // Update animation latency (simplified)
    this.realTimeMetrics.animationLatency = frameTime;

    // Update memory pressure
    this.updateMemoryPressure();

    // Update GPU utilization estimate
    this.updateGPUUtilization();

    // Update CPU utilization estimate
    this.updateCPUUtilization();

    // Notify listeners
    if (this.onPerformanceUpdate) {
      this.onPerformanceUpdate({ ...this.realTimeMetrics });
    }

    // Check if target is being missed
    if (
      this.realTimeMetrics.currentFrameRate <
      this.config.targetFrameRate * 0.9
    ) {
      this.handleTargetMiss();
    }
  }

  /**
   * Update memory pressure metrics
   */
  private updateMemoryPressure(): void {
    if ("memory" in performance) {
      const memory = (performance as any).memory;
      const usedHeap = memory.usedJSHeapSize;
      const totalHeap = memory.totalJSHeapSize;
      const heapLimit = memory.jsHeapSizeLimit;

      // Calculate memory pressure as percentage of limit
      this.realTimeMetrics.memoryPressure = (usedHeap / heapLimit) * 100;
    }
  }

  /**
   * Estimate GPU utilization based on rendering complexity
   */
  private updateGPUUtilization(): void {
    // Simplified GPU utilization estimation
    const gpuLayers = document.querySelectorAll(
      '[style*="will-change"], [style*="transform3d"], [style*="translateZ"]',
    );
    const activeAnimations = document.querySelectorAll(
      '[style*="animation"], [style*="transition"]',
    );

    // Estimate based on active GPU-accelerated elements
    const complexity = gpuLayers.length * 2 + activeAnimations.length;
    this.realTimeMetrics.gpuUtilization = Math.min(
      (complexity / 50) * 100,
      100,
    );
  }

  /**
   * Estimate CPU utilization based on JavaScript execution
   */
  private updateCPUUtilization(): void {
    // Simplified CPU utilization estimation
    const frameTime = this.realTimeMetrics.animationLatency;
    const targetFrameTime = 1000 / this.config.targetFrameRate;

    // If frame takes longer than target, assume higher CPU usage
    this.realTimeMetrics.cpuUtilization = Math.min(
      (frameTime / targetFrameTime) * 50,
      100,
    );
  }

  /**
   * Handle when performance target is missed
   */
  private handleTargetMiss(): void {
    if (this.onTargetMissed && this.frameCount > 30) {
      // Only after sufficient samples
      const preliminaryResult = this.calculateValidationResult();
      this.onTargetMissed(preliminaryResult);
    }
  }

  /**
   * Complete validation and generate results
   */
  private completeValidation(): void {
    if (!this.isMonitoring) return;

    this.stopValidation();

    // Ensure we have enough data for validation
    if (this.frameTimestamps.length < 10) {
      console.warn(
        "PerformanceValidator: Insufficient frame data for validation",
      );
      return;
    }

    try {
      const result = this.calculateValidationResult();
      this.validationResults.push(result);

      if (this.onValidationComplete) {
        this.onValidationComplete(result);
      }

      if (this.config.enablePerformanceReporting) {
        this.generatePerformanceReport(result);
      }
    } catch (error) {
      console.error(
        "PerformanceValidator: Error completing validation:",
        error,
      );
    }

    // Continue monitoring if configured (with safety check)
    if (
      this.config.enableContinuousMonitoring &&
      this.validationResults.length < 10
    ) {
      setTimeout(() => {
        if (this.config.enableContinuousMonitoring) {
          // Double check
          this.startValidation();
        }
      }, 5000); // Increased to 5 second break between validations
    }
  }

  /**
   * Calculate validation result
   */
  private calculateValidationResult(): PerformanceValidationResult {
    if (this.frameTimestamps.length < 2) {
      throw new Error("Insufficient frame data for validation");
    }

    // Calculate actual frame rate
    const totalDuration =
      this.frameTimestamps[this.frameTimestamps.length - 1] -
      this.frameTimestamps[0];
    const actualFrameRate =
      ((this.frameTimestamps.length - 1) * 1000) / totalDuration;

    // Calculate frame times
    const frameTimes: number[] = [];
    for (let i = 1; i < this.frameTimestamps.length; i++) {
      frameTimes.push(this.frameTimestamps[i] - this.frameTimestamps[i - 1]);
    }

    // Calculate statistics
    const averageFrameTime =
      frameTimes.reduce((sum, time) => sum + time, 0) / frameTimes.length;
    const worstFrameTime = Math.max(...frameTimes);

    // Calculate 95th percentile frame time
    const sortedFrameTimes = [...frameTimes].sort((a, b) => a - b);
    const percentile95Index = Math.floor(sortedFrameTimes.length * 0.95);
    const percentile95FrameTime = sortedFrameTimes[percentile95Index];

    // Determine if target is met
    const isTargetMet =
      actualFrameRate >= this.config.targetFrameRate * 0.95 &&
      this.droppedFrames <= this.config.acceptableDropThreshold;

    // Calculate confidence score
    const frameRateConfidence = Math.min(
      actualFrameRate / this.config.targetFrameRate,
      1,
    );
    const dropConfidence = Math.max(
      0,
      1 - this.droppedFrames / this.config.acceptableDropThreshold,
    );
    const confidence = (frameRateConfidence + dropConfidence) / 2;

    // Generate recommendations
    const recommendations = this.generateRecommendations(
      actualFrameRate,
      this.droppedFrames,
      percentile95FrameTime,
    );

    return {
      frameRate: actualFrameRate,
      frameDrops: this.droppedFrames,
      averageFrameTime,
      worstFrameTime,
      percentile95FrameTime,
      isTargetMet,
      confidence,
      recommendations,
    };
  }

  /**
   * Generate performance recommendations
   */
  private generateRecommendations(
    frameRate: number,
    frameDrops: number,
    p95FrameTime: number,
  ): string[] {
    const recommendations: string[] = [];
    const targetFrameTime = 1000 / this.config.targetFrameRate;

    if (frameRate < this.config.targetFrameRate * 0.9) {
      recommendations.push(
        "Frame rate below target - consider reducing animation complexity",
      );
    }

    if (frameDrops > this.config.acceptableDropThreshold) {
      recommendations.push(
        "Excessive frame drops detected - optimize expensive operations",
      );
    }

    if (p95FrameTime > targetFrameTime * 2) {
      recommendations.push(
        "High frame time variance - investigate performance bottlenecks",
      );
    }

    if (this.realTimeMetrics.memoryPressure > 80) {
      recommendations.push(
        "High memory pressure - consider memory optimization",
      );
    }

    if (this.realTimeMetrics.gpuUtilization > 90) {
      recommendations.push(
        "High GPU utilization - reduce GPU-accelerated elements",
      );
    }

    if (recommendations.length === 0) {
      recommendations.push("Performance is meeting targets - excellent job!");
    }

    return recommendations;
  }

  /**
   * Generate detailed performance report
   */
  private generatePerformanceReport(result: PerformanceValidationResult): void {
    console.group("🎯 Visual Feedback Performance Validation Report");
    console.log(`Target Frame Rate: ${this.config.targetFrameRate} fps`);
    console.log(`Actual Frame Rate: ${result.frameRate.toFixed(2)} fps`);
    console.log(
      `Frame Drops: ${result.frameDrops} (threshold: ${this.config.acceptableDropThreshold})`,
    );
    console.log(`Average Frame Time: ${result.averageFrameTime.toFixed(2)}ms`);
    console.log(`Worst Frame Time: ${result.worstFrameTime.toFixed(2)}ms`);
    console.log(
      `95th Percentile Frame Time: ${result.percentile95FrameTime.toFixed(2)}ms`,
    );
    console.log(`Target Met: ${result.isTargetMet ? "✅" : "❌"}`);
    console.log(`Confidence: ${(result.confidence * 100).toFixed(1)}%`);

    if (result.recommendations.length > 0) {
      console.group("📋 Recommendations:");
      result.recommendations.forEach((rec) => console.log(`• ${rec}`));
      console.groupEnd();
    }

    console.group("📊 Real-time Metrics:");
    console.log(
      `Memory Pressure: ${this.realTimeMetrics.memoryPressure.toFixed(1)}%`,
    );
    console.log(
      `GPU Utilization: ${this.realTimeMetrics.gpuUtilization.toFixed(1)}%`,
    );
    console.log(
      `CPU Utilization: ${this.realTimeMetrics.cpuUtilization.toFixed(1)}%`,
    );
    console.groupEnd();

    console.groupEnd();
  }

  /**
   * Get current real-time metrics
   */
  public getRealTimeMetrics(): RealTimeMetrics {
    return { ...this.realTimeMetrics };
  }

  /**
   * Get all validation results
   */
  public getValidationResults(): PerformanceValidationResult[] {
    return [...this.validationResults];
  }

  /**
   * Get latest validation result
   */
  public getLatestResult(): PerformanceValidationResult | null {
    return this.validationResults.length > 0
      ? this.validationResults[this.validationResults.length - 1]
      : null;
  }

  /**
   * Check if performance is currently meeting targets
   */
  public isPerformanceGood(): boolean {
    const latestResult = this.getLatestResult();
    return latestResult ? latestResult.isTargetMet : false;
  }

  /**
   * Get performance confidence score
   */
  public getConfidenceScore(): number {
    const latestResult = this.getLatestResult();
    return latestResult ? latestResult.confidence : 0;
  }

  /**
   * Force immediate validation
   */
  public validateNow(): Promise<PerformanceValidationResult> {
    return new Promise((resolve, reject) => {
      if (this.isMonitoring) {
        reject(new Error("Validation already in progress"));
        return;
      }

      const originalCallback = this.onValidationComplete;
      this.onValidationComplete = (result) => {
        this.onValidationComplete = originalCallback;
        resolve(result);
      };

      this.startValidation();
    });
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<PerformanceValidationConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Dispose of performance validator
   */
  public dispose(): void {
    // Force stop all monitoring
    this.isMonitoring = false;
    this.config.enableContinuousMonitoring = false;

    // Cancel any pending animation frames
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear all data
    this.validationResults = [];
    this.frameTimestamps = [];
    this.resetMetrics();

    // Clear callbacks
    this.onPerformanceUpdate = undefined;
    this.onTargetMissed = undefined;
    this.onValidationComplete = undefined;
  }
}
